import { SubDepartment } from "@/modules/common/types";
import { UUID } from "crypto";

export interface CommonSalesDetailsSummary {
  directorTargetMultiplier: number | null;
  totalSales: number;
  totalReserved: number;
  totalEoi: number;
  totalBlocked: number;
  weightedFirstAction: number;
}

export interface QueryTargetAggregate {
  subDepartmentUId?: string;
  from: string;
  to: string;
  organizationUIds?: UUID[];
}

export interface CommonTargetInfo {
  commercial_achieved: number;
  commercial_target: number;
  eoi: number;
  eoi_detailed: Array<any>;
  finance_achieved: number;
  finance_target: number;
  primary_achieved: number;
  primary_target: number;
  resale_achieved: number;
  resale_target: number;
  reserved_detailed: Array<any>;
  sales_detailed: Array<any>;
  target: number;
  title: string;
  total_reserved: number;
  total_sales: number;
  unitblocked: number;
  unitblocked_detailed: Array<any>;
}

export interface ConfigFactors {
  eoiFactor: number;
  reservedFactor: number;
  blockedFactor: number;
}

export interface FactorResponse {
  data: {
    conf_value: string;
  };
}

export interface QuarterInfo {
  quarterNumber: 1 | 2 | 3 | 4;
  startDate: Date;
  endDate: Date;
  year: number;
  quarterLabel: string;
  monthRange: string;
  m1: string;
  m2: string;
  m3: string;
  m1StartDate: Date;
  m2StartDate: Date;
  m3StartDate: Date;
  m1EndDate: Date;
  m2EndDate: Date;
  m3EndDate: Date;
}

export interface MonthInfo {
  monthNumber: number;
  startDate: Date;
  endDate: Date;
  year: number;
  monthLabel: string;
  monthShort: string;
  monthLong: string;
  daysInMonth: number;
}

export enum TargetType {
  MONTHLY = "Monthly",
  QUARTERLY = "Quarterly",
}

export type SaleTypeName =
  | "Contracted"
  | "Reserved"
  | "Blocked"
  | "EOI"
  | "Potential";

export interface Multiplier {
  from: string;
  to: string;
  amount: number;
}

export interface CheckMultiplierResponse {
  data: Array<Multiplier>;
}

export interface NewHireTarget {
  from: Date;
  to: Date;
  mainAmount: number;
  remainingAmount: number;
}

export interface NewHireTargetResponse {
  data: Array<NewHireTarget>;
}

export interface CommonTarget {
  id: number;
  SubDepartment: SubDepartment;
  from: Date;
  to: Date;
  amount: number;
  organziationUId: string;
}

export type GetOrganizationTargetResponse = {
  data: Array<CommonTarget>;
};

interface AgentsOrganizationTargetResponse {
  id: number;
  SubDepartment: {
    name: string;
    uid: string;
    hasTarget: boolean;
  };
  Rank: {
    title: string;
    uid: string;
  };
  from: Date;
  to: Date;
  amount: number;
  minimumTarget?: number;
  organziationUId: string;
}

export interface GetAgentTargetResponse {
  data: Array<AgentsOrganizationTargetResponse>;
}

interface Rank {
  title: string;
  uid: string;
}

interface TargetMultiplier {
  id: number;
  SubDepartment: SubDepartment;
  Rank: Rank;
  from: string;
  to: string;
  count: number;
  percentage: number;
  organizationUId: string;
}

export interface TargetMultiplierResponse {
  data: Array<TargetMultiplier>;
}

export enum TargetTypes {
  GMV = "gmv",
  REVENUE = "revenue",
  LOAN_AMOUNT = "loanAmount",
}

export interface TargetAggregate {
  organizationAchievement: number;
  organizationName: string;
  organizationTarget: number;
  organizationUId: string;
  personalAchievement: number;
  personalTarget: number;
  teamAchievement: number;
  teamTarget: number;
  effectiveTarget: number;
}

export interface TargetsOverviewBaseProps {
  isMonthlyAggregateLoading: boolean;
  isQuarterlyAggregateLoading: boolean;
  monthlySelectedFilter: string;
  quarterlySelectedFilter: string;
  handleMonthlyFilterChange: (filter: string) => void;
  handleQuarterlyFilterChange: (filter: string) => void;
}
export interface SingleOrganizationTargetsOverviewProps
  extends TargetsOverviewBaseProps {
  monthlyAggregate: TargetAggregate;
  quarterlyAggregate: TargetAggregate;
}

export interface MultiOrganizationTargetsOverviewProps
  extends TargetsOverviewBaseProps {
  monthlyAggregate: TargetAggregate[];
  quarterlyAggregate: TargetAggregate[];
}

export interface ProccessedTargets {
  name: string;
  uid: string;
  rankName?: string;
  targets: {
    id: number;
    from: Date;
    to: Date;
    amount: number;
  }[];
  totalSubdepartmentTarget: number;
}

export interface CombinedDataItem {
  uid: string;
  name: string;
  organizationTargets: ProccessedTargets[];
  agentTargets: ProccessedTargets[];
  targetMultipliers: TargetMultiplierResponse[];
}

export enum TargetTemplateType {
  PERSONAL = "personal",
  MINIMUM = "minimum",
}
